import React, { Dispatch, SetStateAction } from 'react';
import { Button, Dialog, Divider, Grid2, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useForm, SubmitHandler } from 'react-hook-form';
import { UserFormValues } from '../../user/models/user';
import { CloseIcon } from '../../shared/components/AppIcons';
import * as HttpStatus from 'http-status-codes';
import theme from '../../../core/theme';

interface Props {
  confirm: (email: string) => void;
  setShowAlert: Dispatch<SetStateAction<boolean>>;
  setErrorMessage: Dispatch<SetStateAction<string>>;
}

const useStyles = makeStyles({
  paper: {
    minWidth: '593px !important',
    minHeight: '137px !important',
    borderRadius: '21px',
    boxShadow: 'rgba(242, 242, 242, 1)',
    padding: '16px',
  },
  close: {
    fontSize: '35px !important',
    color: '#5F6774 !important',
    '& path': {
      fill: `#2C2C2C !important`,
    },
  },
  text: {
    fontSize: '16px',
    '& .highlightedText': {
      color: `${theme.palette.error.main} !important`,
    },
  },
  bold: {
    fontWeight: 'bolder !important',
  },
  button: {
    height: '38px',
    fontSize: '18px !important',
    fontFamily: 'Lato !important',
    backgroundColor: '#F67570 !important',
    boxShadow: 'none !important',
    color: '#fff !important',
    '& g path': {
      fill: '#ffffff !important',
    },
  },
  cancel: {
    fontSize: '16px !important',
    color: '#F67570 !important',
    marginRight: '24px !important',
  },
});

const ForgotPasswordModal: React.FC<Props> = ({ confirm, setShowAlert, setErrorMessage }) => {
  const classes = useStyles();
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<UserFormValues>({ mode: 'onBlur' });

  const onSubmit: SubmitHandler<{ email: string }> = async (form) => {
    try {
      setErrorMessage('');
      confirm(form.email);
    } catch (error) {
      if (error?.response?.status === HttpStatus.NOT_FOUND) {
        setErrorMessage('Invalid user');
      } else {
        setErrorMessage('Please try again');
      }
    }
  };

  return (
    <Dialog
      open
      aria-modal="true"
      role="dialog"
      onClose={() => setShowAlert(false)}
      PaperProps={{
        onClick: (e: any) => e.stopPropagation(),
        className: classes.paper,
      }}
    >
      <Grid2 container justifyContent="space-between">
        <Grid2 container className={classes.bold}>
          <span style={{ fontWeight: 'inherit' }}>Forgot Password?</span>
        </Grid2>
        <Grid2 container>
          <IconButton onClick={() => setShowAlert(false)} role="button" sx={{ padding: 0 }}>
            <CloseIcon viewBox="0 0 42 42" className={classes.close} />
          </IconButton>
        </Grid2>
      </Grid2>

      <Grid2 mb={1}>
        <Divider
          orientation="horizontal"
          flexItem
          sx={{
            backgroundColor: '#E1E3E8',
            paddingBottom: '1px',
          }}
        />
      </Grid2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid2 p={1}>
          <Grid2 container direction="column" mb={2}>
            <span>Enter your registered email address and we'll send you a link to reset your password</span>
          </Grid2>

          <Grid2 container mt={3} direction="column">
            <label htmlFor="inputEmail" className={classes.text}>
              Email Address*
              <input
                {...register('email', {
                  required: 'Email is Required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|[A-Z0-9_-]$/i,
                    message: 'Invalid email address',
                  },
                })}
                name="email"
                id="inputEmail"
                className={`form-control ${errors.email && 'is-invalid'}`}
                placeholder="Enter your Email Address"
              />
              {errors.email && <span>{errors.email.message}</span>}
            </label>
          </Grid2>
        </Grid2>

        <Grid2 container justifyContent="end" mt={3}>
          <Button variant="text" type="button" className={classes.cancel} onClick={() => setShowAlert(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            className={classes.button}
            style={{ width: 'auto', textTransform: 'none' }}
          >
            Send Reset Link
          </Button>
        </Grid2>
      </form>
    </Dialog>
  );
};

export default ForgotPasswordModal;
